import * as THREE from 'three'
import type {
  GraphicsRegionCaptureResult,
  GraphicsRegionCaptureSession,
} from '@/graphics/GraphicsBackend'
import type { EngineContext } from '@/runtime/EngineContext'
import type { EventBus } from '@/runtime/EventBus'
import type { SceneGraphService } from '@/runtime/SceneGraphService'
import { getLoggerManager } from '@/shared/logging'
import type { ViewFrameContext, ViewRect, ViewSnapshot } from '@/shared/types/view'
import { toRendererViewportRect } from '@/shared/utils/viewRect'

const log = getLoggerManager().getLogger('magnifier')

/**
 * WebGPU 采集的最小间隔。
 *
 * 采集已把栅格范围收敛到读回子区域，单次成本很低，因此不再低频限速：0 表示
 * 「输入变化即采集」，优先保证放大镜跟手（异步读回本身约有 1 帧延迟）。
 */
const WEBGPU_CAPTURE_MIN_INTERVAL_MS = 0

/**
 * 放大镜构造参数。
 */
export interface MagnifierOptions {
  /** 放大镜显示半径，单位 CSS 像素 */
  radius?: number
  /** 相对于原始画面的放大倍数 */
  magnification?: number
  /** 是否在放大渲染时跳过文本精灵 */
  excludeTextSprite?: boolean
  /** 自定义排除谓词，返回 true 的对象在放大渲染时临时隐藏 */
  excludeObjectPredicate?: (obj: THREE.Object3D) => boolean
  occlusionExcludeObjectPredicate?: (obj: THREE.Object3D) => boolean
}

type RenderPipelineLike = {
  execute: (
    frame: ViewFrameContext,
    options?: {
      outputTarget?: THREE.WebGLRenderTarget | null
      viewportOverride?: { x: number; y: number; width: number; height: number }
    }
  ) => void
}

type CaptureKind = 'sample' | 'underlay'

/** 一次已同步发起的采集：渲染已发生，只等读回与画布写入。 */
interface CaptureRequest {
  readonly kind: CaptureKind
  readonly promise: Promise<{ canvas: HTMLCanvasElement; width: number; height: number }>
}

export class Magnifier {
  private _context: EngineContext
  private _events: EventBus
  private _sceneGraph: SceneGraphService
  private _getActiveCamera: () => THREE.Camera | undefined

  private _canvas: HTMLCanvasElement
  private _ctx: CanvasRenderingContext2D
  private _sampleCanvas: HTMLCanvasElement
  private _sampleCtx: CanvasRenderingContext2D
  private _underlayCanvas: HTMLCanvasElement
  private _underlayCtx: CanvasRenderingContext2D
  private _captureSessions = new Map<CaptureKind, GraphicsRegionCaptureSession>()
  private _captureSupported: boolean | null = null
  private _captureInFlight = false
  private _disposed = false
  private _lastFrameTimestamp = 0
  private _lastFrameDelta = 0
  /** 上一次成功发起的采集输入签名；输入未变化时不做重复采集。 */
  private _lastCaptureSignature: string | null = null
  private _lastCaptureStartedAt = Number.NEGATIVE_INFINITY
  private _radius: number
  private _magnification: number
  private _visible = false
  private _mouseX = 0
  private _mouseY = 0
  private _offsetY = 30
  private _originalCursor?: string
  private _options: MagnifierOptions

  private _onRenderBound: (payload?: {
    engineFrame?: { timestamp?: number; delta?: number }
  }) => Promise<void> | void
  private _onMouseMoveBound: (e: MouseEvent) => void

  constructor(
    context: EngineContext,
    getActiveCamera: () => THREE.Camera | undefined,
    options: MagnifierOptions = {}
  ) {
    const { radius = 100, magnification = 3 } = options
    this._context = context
    this._events = context.events
    this._sceneGraph = context.sceneGraph
    this._getActiveCamera = getActiveCamera
    this._radius = radius
    this._magnification = magnification
    this._options = options

    const size = this._radius * 2
    this._canvas = document.createElement('canvas')
    this._canvas.width = size
    this._canvas.height = size
    Object.assign(this._canvas.style, {
      position: 'absolute',
      pointerEvents: 'none',
      borderRadius: '50%',
      border: '2px solid rgba(255, 255, 255, 0.8)',
      boxShadow: '0 0 8px rgba(0, 0, 0, 0.5)',
      display: 'none',
    })

    this._ctx = this._canvas.getContext('2d')!
    this._ctx.imageSmoothingEnabled = false
    this._context.container.appendChild(this._canvas)

    this._sampleCanvas = document.createElement('canvas')
    this._sampleCtx = this._sampleCanvas.getContext('2d')!
    this._sampleCtx.imageSmoothingEnabled = false

    this._underlayCanvas = document.createElement('canvas')
    this._underlayCtx = this._underlayCanvas.getContext('2d')!
    this._underlayCtx.imageSmoothingEnabled = false

    this._onRenderBound = this._onRenderFinished.bind(this)
    this._onMouseMoveBound = this._onMouseMove.bind(this)
  }

  /** 放大镜当前是否可见。 */
  get isVisible(): boolean {
    return this._visible
  }

  /**
   * 显示放大镜并开始订阅帧渲染与鼠标事件。
   */
  show() {
    if (this._visible) return
    this._visible = true
    this._lastCaptureSignature = null
    this._canvas.style.display = 'block'
    this._syncCanvasPosition()
    this._events.on('frame.end', this._onRenderBound)
    this._hostCanvas().addEventListener('mousemove', this._onMouseMoveBound)
    this._originalCursor = this._hostCanvas().style.cursor
    this._hostCanvas().style.cursor = 'crosshair'
  }

  /**
   * 隐藏放大镜并注销帧渲染与鼠标事件订阅。
   */
  hide() {
    if (!this._visible) return
    this._visible = false
    this._canvas.style.display = 'none'
    this._events.off('frame.end', this._onRenderBound)
    this._hostCanvas().removeEventListener('mousemove', this._onMouseMoveBound)
    this._hostCanvas().style.cursor = this._originalCursor ?? ''
    this._originalCursor = undefined
  }

  /**
   * 调整放大镜半径并同步更新画布尺寸与位置。
   *
   * @param radius - 新半径，单位 CSS 像素
   */
  setRadius(radius: number) {
    if (radius === this._radius) return
    this._radius = radius
    const size = this._radius * 2
    this._canvas.width = size
    this._canvas.height = size
    this._canvas.style.left = `${this._mouseX - this._radius}px`
    this._canvas.style.top = `${this._mouseY - this._radius * 2 - this._offsetY}px`
  }

  /** 放大镜宿主 canvas：统一走 GraphicsBackend 的 surface canvas。 */
  private _hostCanvas(): HTMLCanvasElement {
    return this._context.graphics.canvas
  }

  private _onMouseMove(e: MouseEvent) {
    const rect = this._hostCanvas().getBoundingClientRect()
    this._mouseX = e.clientX - rect.left
    this._mouseY = e.clientY - rect.top
  }

  /**
   * 发起本帧的采集。
   *
   * 采集的「渲染」必须发生在 frame/View 边界内：RenderLoop 在 `frame.end` 之后才会 `endFrame()`，
   * 而 `await` 会把后续逻辑推到微任务（此时 frame 已关闭）。因此这里同步发起（同步渲染 + 同步
   * 恢复现场），只把读回与合成留给后续异步步骤。
   */
  private _onRenderFinished(payload?: {
    engineFrame?: { timestamp?: number; delta?: number }
  }): Promise<void> | void {
    const engineFrame = payload?.engineFrame
    if (typeof engineFrame?.timestamp === 'number') this._lastFrameTimestamp = engineFrame.timestamp
    if (typeof engineFrame?.delta === 'number') this._lastFrameDelta = engineFrame.delta
    if (!this._visible || this._disposed) return

    // 画布位置每帧跟随鼠标：与采集解耦，指针手感不受读回延迟与采集限速影响。
    this._syncCanvasPosition()

    if (this._captureInFlight) return
    if (!this._shouldCapture()) return

    // DPR 走 backend surface 快照，避免依赖 legacy renderer 的 pixel ratio。
    const scaleFactor = this._context.graphics.surface.pixelRatio
    // 采样先发起：读回调度串行，保证用户直面的内容最早完成。
    const sample = this._startCapture('sample', scaleFactor)
    const underlay = this._startCapture('underlay', scaleFactor)
    if (!sample) return

    this._captureInFlight = true
    return this._finishCapture(underlay, sample)
  }

  /**
   * 判断本帧是否需要发起采集。
   *
   * 采集会重跑整条管线（WebGPU 还要异步读回），因此只在输入真正变化时采集；WebGPU 另外加
   * 最小间隔，避免移动鼠标时把每帧预算都吃掉。
   */
  private _shouldCapture(): boolean {
    const scaleFactor = this._context.graphics.surface.pixelRatio
    const signature = this._captureSignature(scaleFactor)
    if (signature === this._lastCaptureSignature) return false

    const minInterval =
      this._context.graphics.kind === 'webgpu' ? WEBGPU_CAPTURE_MIN_INTERVAL_MS : 0
    if (this._lastFrameTimestamp - this._lastCaptureStartedAt < minInterval) return false

    this._lastCaptureSignature = signature
    this._lastCaptureStartedAt = this._lastFrameTimestamp
    return true
  }

  /** 采集输入签名：鼠标（设备像素）、View 区域、绘制缓冲尺寸、相机矩阵与缩放。 */
  private _captureSignature(scaleFactor: number): string {
    const surface = this._context.graphics.surface
    const view = this._resolveCaptureViewRect()
    const parts: (number | string)[] = [
      Math.round(this._mouseX * scaleFactor),
      Math.round(this._mouseY * scaleFactor),
      view.x,
      view.y,
      view.width,
      view.height,
      surface.drawingBufferWidth,
      surface.drawingBufferHeight,
      this._radius,
      this._magnification,
    ]

    const camera = this._getActiveCamera()
    if (camera) {
      parts.push(...camera.matrixWorld.elements, ...camera.projectionMatrix.elements)
      parts.push((camera as THREE.PerspectiveCamera).zoom ?? 1)
    }
    return parts.join(',')
  }

  private async _finishCapture(
    underlay: CaptureRequest | null,
    sample: CaptureRequest
  ): Promise<void> {
    try {
      const [underlayResult, sampleResult] = await Promise.all([
        underlay?.promise ?? Promise.resolve(null),
        sample.promise,
      ])
      if (!sampleResult || !this._visible || this._disposed) return
      this._composeMagnifier(sampleResult, underlayResult)
    } catch (error) {
      log.warn(`event=magnifier.capture.failed error=${describeError(error)}`)
    } finally {
      this._captureInFlight = false
    }
  }

  /** 把放大镜内容绘制到展示画布，并同步跟随鼠标位置。 */
  private _composeMagnifier(
    source: { canvas: HTMLCanvasElement; width: number; height: number },
    underlay: { canvas: HTMLCanvasElement; width: number; height: number } | null
  ): void {
    const size = this._radius * 2
    const ctx = this._ctx
    ctx.clearRect(0, 0, size, size)
    ctx.imageSmoothingEnabled = false

    ctx.save()
    ctx.beginPath()
    ctx.arc(this._radius, this._radius, this._radius, 0, Math.PI * 2)
    ctx.clip()

    if (underlay) {
      ctx.drawImage(underlay.canvas, 0, 0, underlay.width, underlay.height, 0, 0, size, size)
    }
    ctx.drawImage(source.canvas, 0, 0, source.width, source.height, 0, 0, size, size)
    ctx.restore()

    this._drawCrosshair(ctx)
  }

  /** 放大镜画布跟随鼠标；每帧调用，不依赖采集结果。 */
  private _syncCanvasPosition(): void {
    this._canvas.style.left = `${this._mouseX - this._radius}px`
    this._canvas.style.top = `${this._mouseY - this._radius * 2 - this._offsetY}px`
  }

  /**
   * 同步发起一次采集：渲染在 frame 边界内完成，仅读回结果由返回的 Promise 决定。
   *
   * 每次调用都复用按用途划分的 session（sample/underlay 各自一个），避免两类采集互相触发
   * newest-wins 取消。
   */
  private _startCapture(kind: CaptureKind, scaleFactor: number): CaptureRequest | null {
    const size = this._radius * 2
    const predicate =
      kind === 'underlay'
        ? this._resolveOcclusionExcludePredicate()
        : this._resolveExcludePredicate()
    if (kind === 'underlay' && !predicate) return null

    // 底图只用于「主对象被遮挡时露出背景」：如果遮挡谓词要隐藏的对象，采样谓词本来就都会隐藏，
    // 两张图完全一致，直接跳过可省掉一半的整管线重跑（WebGPU 上尤其明显；测量场景里
    // 遮挡谓词通常只命中文字精灵，正属于这种情况）。
    const underlayCandidates =
      kind === 'underlay' && predicate ? this._collectHideCandidates(predicate) : null
    if (underlayCandidates) {
      const samplePredicate = this._resolveExcludePredicate()
      const needsUnderlay = underlayCandidates.some((obj) => !samplePredicate?.(obj))
      if (!needsUnderlay) return null
    }

    const session = this._ensureCaptureSession(kind)
    if (!session) return null

    const canvas = kind === 'underlay' ? this._underlayCanvas : this._sampleCanvas
    const ctx = kind === 'underlay' ? this._underlayCtx : this._sampleCtx
    const sourceCSS = kind === 'underlay' ? size : size / this._magnification

    const surface = this._context.graphics.surface
    const totalW = Math.max(1, Math.round(surface.drawingBufferWidth))
    const totalH = Math.max(1, Math.round(surface.drawingBufferHeight))
    const sw = Math.max(1, Math.floor(sourceCSS * scaleFactor))
    const sh = Math.max(1, Math.floor(sourceCSS * scaleFactor))
    const sx = Math.floor(this._mouseX * scaleFactor - sw / 2)
    const sy = Math.floor(this._mouseY * scaleFactor - sh / 2)
    const clamped = this._clampReadRect(sx, sy, sw, sh, totalW, totalH)
    const captureView = this._resolveCaptureViewRect()

    let hiddenObjects: THREE.Object3D[] = []
    const restoreObjects = () => {
      for (const obj of hiddenObjects) {
        obj.visible = true
      }
      hiddenObjects = []
    }

    const promise = session
      .capture({
        width: totalW,
        height: totalH,
        readRect: { x: clamped.x, y: clamped.yTop, width: clamped.w, height: clamped.h },
        viewRect: captureView,
        beforeRender: () => {
          hiddenObjects = underlayCandidates
            ? this._hideObjects(underlayCandidates)
            : this._temporarilyHideObjects(predicate)
        },
        afterRender: restoreObjects,
        render: () => this._renderOffscreen(),
      })
      .then((result) => this._paintCapture(canvas, ctx, result))
      .catch((error: unknown) => {
        // 失败/取消路径也必须恢复被排除对象（session 的 afterRender 已覆盖常规路径）。
        restoreObjects()
        throw error
      })

    return { kind, promise }
  }

  private _paintCapture(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    result: GraphicsRegionCaptureResult
  ): { canvas: HTMLCanvasElement; width: number; height: number } {
    if (canvas.width !== result.width || canvas.height !== result.height) {
      canvas.width = result.width
      canvas.height = result.height
    }
    ctx.putImageData(
      new ImageData(new Uint8ClampedArray(result.pixels), result.width, result.height),
      0,
      0
    )
    return { canvas, width: result.width, height: result.height }
  }

  /**
   * 在当前 backend-owned 采集目标上重跑当前 View 的渲染。
   *
   * 目标与 viewport/scissor 都由 capture session 绑定，因此这里按普通 View 帧执行管线，
   * 不走 legacy `outputTarget`/`viewportOverride` 逃逸口（WebGPU 明确拒绝该逃逸口）。
   */
  private _renderOffscreen() {
    const activeCamera = this._getActiveCamera()
    if (!activeCamera) return

    const pipeline = this._getRenderPipeline()
    const frame = this._buildPipelineFrame(activeCamera)

    if (pipeline && frame) {
      pipeline.execute(frame)
      return
    }

    // 无 RenderPipeline 时的 legacy 兜底只对 WebGL 成立：WebGPU 下不存在 legacy renderer。
    if (this._context.graphics.kind !== 'webgl2') {
      log.debug('event=magnifier.capture.skip reason=no-render-pipeline')
      return
    }

    const sg = this._sceneGraph
    const renderer = this._context.renderer
    renderer.render(sg.mainScene, activeCamera)
    renderer.clearDepth()
    renderer.render(sg.overlayScene, activeCamera)
  }

  /** 按用途复用 backend 的区域采集 session；backend 不支持时返回 null。 */
  private _ensureCaptureSession(kind: CaptureKind): GraphicsRegionCaptureSession | null {
    const existing = this._captureSessions.get(kind)
    if (existing) return existing

    if (this._captureSupported === null) {
      this._captureSupported =
        typeof this._context.graphics.createRegionCaptureSession === 'function'
    }
    if (!this._captureSupported) return null

    const session = this._context.graphics.createRegionCaptureSession?.()
    if (!session) return null
    this._captureSessions.set(kind, session)
    return session
  }

  /** 当前 View 的 CSS 像素矩形；无 ViewRegistry 时回退到整个画布。 */
  private _resolveCaptureViewRect(): ViewRect {
    const activeView = this._getActiveViewSnapshot()
    if (activeView) return { ...activeView.rect }
    return {
      x: 0,
      y: 0,
      width: Math.max(1, this._hostCanvas().clientWidth),
      height: Math.max(1, this._hostCanvas().clientHeight),
    }
  }

  /**
   * 构造重跑用的 View 帧，字段与 RenderLoop 的真实帧保持一致。
   *
   * viewport 单位与 `GraphicsBackend.beginView` 相同：WebGL2 用左下原点，WebGPU 用左上原点 ViewRect。
   */
  private _buildPipelineFrame(camera: THREE.Camera): ViewFrameContext {
    const activeView = this._getActiveViewSnapshot()
    const surface = this._context.graphics.surface
    const viewRect = this._resolveCaptureViewRect()
    const viewport =
      this._context.graphics.kind === 'webgl2'
        ? toRendererViewportRect(viewRect, {
            width: Math.max(1, Math.round(surface.logicalWidth)),
            height: Math.max(1, Math.round(surface.logicalHeight)),
          })
        : { ...viewRect }

    // token 必须沿用当前引擎帧号：GSplat 等 contributor 会拒绝回退的逻辑帧。
    const frameNumber = this._context.frameNumber ?? 0
    return {
      frameNumber,
      timestamp: this._lastFrameTimestamp,
      delta: this._lastFrameDelta,
      graphics: this._context.graphics,
      viewId: activeView?.id ?? 'primary',
      camera,
      // WebGPU 下不存在 legacy renderer：这里只填类型占位，contributor 读取时由 legacy accessor 抛结构化错误。
      renderer:
        this._context.graphics.kind === 'webgl2'
          ? this._context.renderer
          : (undefined as unknown as THREE.WebGLRenderer),
      viewport,
      viewRect,
    }
  }

  private _getActiveViewSnapshot():
    | (ViewSnapshot & { viewport?: { x: number; y: number; width: number; height: number } })
    | null {
    if (!this._context.hasService?.('ViewRegistry')) {
      return null
    }

    try {
      const registry = this._context.getService('ViewRegistry') as {
        getActive?: () =>
          | (ViewSnapshot & {
              viewport?: { x: number; y: number; width: number; height: number }
            })
          | undefined
      }
      return registry.getActive?.() ?? null
    } catch {
      return null
    }
  }

  private _getRenderPipeline(): RenderPipelineLike | null {
    if (!this._context.hasService?.('RenderPipeline')) {
      return null
    }

    try {
      return this._context.getService('RenderPipeline') as unknown as RenderPipelineLike
    } catch {
      return null
    }
  }

  /** 把读取区域收敛到绘制缓冲范围内，返回 top-left 原点坐标。 */
  private _clampReadRect(
    x: number,
    yTop: number,
    w: number,
    h: number,
    totalW: number,
    totalH: number
  ) {
    const maxX = Math.max(0, totalW - w)
    const maxTop = Math.max(0, totalH - h)
    const clampedX = Math.min(Math.max(0, x), maxX)
    const clampedTop = Math.min(Math.max(0, yTop), maxTop)
    return { x: clampedX, yTop: clampedTop, w, h }
  }

  private _temporarilyHideObjects(
    predicate: ((obj: THREE.Object3D) => boolean) | null
  ): THREE.Object3D[] {
    if (!predicate) return []
    return this._hideObjects(this._collectHideCandidates(predicate))
  }

  /** 收集会被临时隐藏的候选对象；不修改可见性。 */
  private _collectHideCandidates(predicate: (obj: THREE.Object3D) => boolean): THREE.Object3D[] {
    const candidates: THREE.Object3D[] = []
    for (const scene of this._collectCandidateScenes()) {
      scene.traverse((obj) => {
        if (!obj.visible) return
        if (!predicate(obj)) return
        candidates.push(obj)
      })
    }
    return candidates
  }

  /** 临时隐藏候选对象，返回后由调用方恢复。 */
  private _hideObjects(candidates: readonly THREE.Object3D[]): THREE.Object3D[] {
    const hidden: THREE.Object3D[] = []
    for (const obj of candidates) {
      if (!obj.visible) continue
      obj.visible = false
      hidden.push(obj)
    }
    return hidden
  }

  private _resolveExcludePredicate(): ((obj: THREE.Object3D) => boolean) | null {
    const custom = this._options.excludeObjectPredicate
    if (custom) return custom
    if (this._options.excludeTextSprite) {
      return (obj: THREE.Object3D) => obj instanceof THREE.Sprite
    }
    return null
  }

  private _resolveOcclusionExcludePredicate(): ((obj: THREE.Object3D) => boolean) | null {
    return this._options.occlusionExcludeObjectPredicate ?? null
  }

  private _collectCandidateScenes(): THREE.Scene[] {
    const sg = this._sceneGraph
    return [sg.mainScene, sg.overlayScene]
  }

  private _drawCrosshair(ctx: CanvasRenderingContext2D) {
    const cx = this._radius
    const cy = this._radius
    const len = 15

    ctx.save()
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)'
    ctx.lineWidth = 1.5

    ctx.beginPath()
    ctx.moveTo(cx - len, cy)
    ctx.lineTo(cx + len, cy)
    ctx.stroke()

    ctx.beginPath()
    ctx.moveTo(cx, cy - len)
    ctx.lineTo(cx, cy + len)
    ctx.stroke()

    ctx.restore()
  }

  /**
   * 释放资源，销毁离屏渲染目标并从容器中移除画布。
   */
  dispose() {
    this.hide()
    this._disposed = true
    for (const session of this._captureSessions.values()) {
      session.dispose()
    }
    this._captureSessions.clear()
    if (this._canvas.parentElement) {
      this._canvas.parentElement.removeChild(this._canvas)
    }
  }
}

function describeError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  return typeof error
}
